/**
 * Website option-set dictionaries (Settings → «Справочники на сайте»).
 * Lives in storefront.moduleSettings.siteDictionaries.
 */

import type { StorefrontTitleI18n } from './storefrontAppearance';
import {
  normalizeAppearanceFontWeight,
  normalizeAppearancePercentScale,
} from './storefrontAppearance';
import type { StorefrontCatalogSiteSettings } from './storefrontCatalogSite';

export const SITE_DICTIONARY_DISPLAY_MODES = ['chips', 'tiles'] as const;
export type SiteDictionaryDisplayMode = (typeof SITE_DICTIONARY_DISPLAY_MODES)[number];

export const SITE_DICTIONARY_TITLE_ALIGNS = ['start', 'center'] as const;
export type SiteDictionaryTitleAlign = (typeof SITE_DICTIONARY_TITLE_ALIGNS)[number];

export const SITE_DICTIONARY_TITLE_SCALE_MIN = 100;
export const SITE_DICTIONARY_TITLE_SCALE_MAX = 500;

export const SITE_DICTIONARY_TILE_SIZE_MIN = 72;
export const SITE_DICTIONARY_TILE_SIZE_MAX = 280;
export const SITE_DICTIONARY_TILE_GAP_MIN = 0;
export const SITE_DICTIONARY_TILE_GAP_MAX = 48;
export const SITE_DICTIONARY_TILES_PER_ROW_MIN = 2;
export const SITE_DICTIONARY_TILES_PER_ROW_MAX = 8;

export type StorefrontSiteDictionariesSettings = {
  /** Master switch: menu item + recommendations screen. */
  enabled: boolean;
  /** Option-set keys used as website filters / recommendations. */
  optionSetFilterKeys: string[];
  /** Chips or tiles in catalog / search overlay (when those surfaces are on). */
  displayMode: SiteDictionaryDisplayMode;
  /** When chips: colorful accents vs theme mono. Default theme (false). */
  chipsColorful: boolean;
  /** Show filter chips/tiles under catalog search. */
  showInCatalog: boolean;
  /** Show filter chips/tiles in menu search overlay. */
  showInSearch: boolean;
  /** Show attached values on product card. */
  showOnProductCard: boolean;
  /** Menu item + screen title; empty lang → locale default «Рекомендации». */
  menuTitleI18n: StorefrontTitleI18n;
  /** Show H2 on the recommendations screen. */
  showScreenTitle: boolean;
  /** Screen title size vs module heading (100 = same as catalog module title). */
  titleFontScale: number;
  titleFontWeight: number;
  titleAlign: SiteDictionaryTitleAlign;
  /** Recommendations screen tile edge length (px) — max cell size. */
  tileSizePx: number;
  /** Gap between recommendation tiles (px). */
  tileGapPx: number;
  /** How many recommendation tiles in one row. */
  tilesPerRow: number;
  /** Tile row alignment on the recommendations screen. */
  tileAlign: SiteDictionaryTitleAlign;
  /** Caption lift on tiles: 0 = below, 50 = center, 100 = above (catalog site pattern). */
  tileCaptionLiftPercent: number;
  tileCaptionFontScale: number;
  tileCaptionFontWeight: number;
  /** Padding inside the tile image box (px). */
  tileImageInsetPx: number;
};

const EMPTY_I18N: StorefrontTitleI18n = { ru: '', en: '', he: '' };

export const DEFAULT_STOREFRONT_SITE_DICTIONARIES: StorefrontSiteDictionariesSettings = {
  enabled: true,
  optionSetFilterKeys: ['application'],
  displayMode: 'chips',
  chipsColorful: false,
  showInCatalog: true,
  showInSearch: true,
  showOnProductCard: true,
  menuTitleI18n: { ...EMPTY_I18N },
  showScreenTitle: true,
  titleFontScale: 100,
  titleFontWeight: 600,
  titleAlign: 'start',
  tileSizePx: 140,
  tileGapPx: 12,
  tilesPerRow: 4,
  tileAlign: 'start',
  tileCaptionLiftPercent: 0,
  tileCaptionFontScale: 100,
  tileCaptionFontWeight: 600,
  tileImageInsetPx: 0,
};

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function normalizeTitleI18n(raw: unknown): StorefrontTitleI18n {
  const source = asRecord(raw);
  return {
    ru: String(source.ru ?? '').trim(),
    en: String(source.en ?? '').trim(),
    he: String(source.he ?? '').trim(),
  };
}

function normalizeKeys(raw: unknown, fallback: string[]): string[] {
  if (!Array.isArray(raw)) return [...fallback];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const key = String(entry || '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

function clampInt(raw: unknown, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function asBool(raw: unknown, fallback: boolean): boolean {
  if (raw === undefined || raw === null) return fallback;
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
}

function normalizeTitleAlign(raw: unknown): SiteDictionaryTitleAlign {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'center') return 'center';
  if (v === 'left' || v === 'start') return 'start';
  return DEFAULT_STOREFRONT_SITE_DICTIONARIES.titleAlign;
}

export function normalizeStorefrontSiteDictionaries(
  raw: unknown,
  legacyCatalogSite?: StorefrontCatalogSiteSettings | null,
): StorefrontSiteDictionariesSettings {
  const source = asRecord(raw);
  const d = DEFAULT_STOREFRONT_SITE_DICTIONARIES;
  const hasOwn = raw && typeof raw === 'object' && !Array.isArray(raw)
    && Object.keys(source).length > 0;

  if (!hasOwn && legacyCatalogSite) {
    return {
      enabled: legacyCatalogSite.showOptionSetFilters !== false,
      optionSetFilterKeys: [...(legacyCatalogSite.optionSetFilterKeys || d.optionSetFilterKeys)],
      displayMode: 'chips',
      chipsColorful: legacyCatalogSite.filterChipsColorful === true,
      showInCatalog: legacyCatalogSite.showOptionSetFilters !== false,
      showInSearch: legacyCatalogSite.showOptionSetFilters !== false,
      showOnProductCard: legacyCatalogSite.showOptionSetsOnProductCard !== false,
      menuTitleI18n: { ...EMPTY_I18N },
      showScreenTitle: d.showScreenTitle,
      titleFontScale: d.titleFontScale,
      titleFontWeight: d.titleFontWeight,
      titleAlign: d.titleAlign,
      tileSizePx: d.tileSizePx,
      tileGapPx: d.tileGapPx,
      tilesPerRow: d.tilesPerRow,
      tileAlign: d.tileAlign,
      tileCaptionLiftPercent: d.tileCaptionLiftPercent,
      tileCaptionFontScale: d.tileCaptionFontScale,
      tileCaptionFontWeight: d.tileCaptionFontWeight,
      tileImageInsetPx: d.tileImageInsetPx,
    };
  }

  const modeRaw = String(source.displayMode ?? source.display_mode ?? '').trim().toLowerCase();
  const displayMode: SiteDictionaryDisplayMode = SITE_DICTIONARY_DISPLAY_MODES.includes(
    modeRaw as SiteDictionaryDisplayMode,
  )
    ? (modeRaw as SiteDictionaryDisplayMode)
    : d.displayMode;

  const enabledRaw = source.enabled ?? source.showOptionSetFilters ?? source.show_option_set_filters;
  const colorfulRaw = source.chipsColorful ?? source.chips_colorful ?? source.filterChipsColorful;
  const productCardRaw = source.showOnProductCard
    ?? source.show_on_product_card
    ?? source.showOptionSetsOnProductCard;
  const inCatalogRaw = source.showInCatalog ?? source.show_in_catalog;
  const inSearchRaw = source.showInSearch ?? source.show_in_search;

  return {
    enabled: asBool(enabledRaw, d.enabled),
    optionSetFilterKeys: normalizeKeys(
      source.optionSetFilterKeys ?? source.option_set_filter_keys,
      d.optionSetFilterKeys,
    ),
    displayMode,
    chipsColorful: asBool(colorfulRaw, d.chipsColorful),
    showInCatalog: asBool(inCatalogRaw, d.showInCatalog),
    showInSearch: asBool(inSearchRaw, d.showInSearch),
    showOnProductCard: asBool(productCardRaw, d.showOnProductCard),
    menuTitleI18n: normalizeTitleI18n(source.menuTitleI18n ?? source.menu_title_i18n),
    showScreenTitle: asBool(
      source.showScreenTitle ?? source.show_screen_title,
      d.showScreenTitle,
    ),
    titleFontScale: normalizeAppearancePercentScale(
      source.titleFontScale ?? source.title_font_scale,
      d.titleFontScale,
      SITE_DICTIONARY_TITLE_SCALE_MIN,
      SITE_DICTIONARY_TITLE_SCALE_MAX,
    ),
    titleFontWeight: normalizeAppearanceFontWeight(
      source.titleFontWeight ?? source.title_font_weight,
      d.titleFontWeight,
    ),
    titleAlign: normalizeTitleAlign(source.titleAlign ?? source.title_align),
    tileSizePx: clampInt(
      source.tileSizePx ?? source.tile_size_px,
      SITE_DICTIONARY_TILE_SIZE_MIN,
      SITE_DICTIONARY_TILE_SIZE_MAX,
      d.tileSizePx,
    ),
    tileGapPx: clampInt(
      source.tileGapPx ?? source.tile_gap_px,
      SITE_DICTIONARY_TILE_GAP_MIN,
      SITE_DICTIONARY_TILE_GAP_MAX,
      d.tileGapPx,
    ),
    tilesPerRow: clampInt(
      source.tilesPerRow ?? source.tiles_per_row,
      SITE_DICTIONARY_TILES_PER_ROW_MIN,
      SITE_DICTIONARY_TILES_PER_ROW_MAX,
      d.tilesPerRow,
    ),
    tileAlign: normalizeTitleAlign(source.tileAlign ?? source.tile_align),
    tileCaptionLiftPercent: clampInt(
      source.tileCaptionLiftPercent ?? source.tile_caption_lift_percent,
      0,
      100,
      d.tileCaptionLiftPercent,
    ),
    tileCaptionFontScale: normalizeAppearancePercentScale(
      source.tileCaptionFontScale ?? source.tile_caption_font_scale,
      d.tileCaptionFontScale,
      100,
      250,
    ),
    tileCaptionFontWeight: normalizeAppearanceFontWeight(
      source.tileCaptionFontWeight ?? source.tile_caption_font_weight,
      d.tileCaptionFontWeight,
    ),
    tileImageInsetPx: clampInt(
      source.tileImageInsetPx ?? source.tile_image_inset_px,
      0,
      32,
      d.tileImageInsetPx,
    ),
  };
}

export function patchSiteDictionaries(
  current: StorefrontSiteDictionariesSettings,
  patch: Partial<StorefrontSiteDictionariesSettings>,
): StorefrontSiteDictionariesSettings {
  return normalizeStorefrontSiteDictionaries({ ...current, ...patch });
}

/** Keep legacy catalogSite option-set fields in sync when dictionaries tab saves. */
export function mirrorSiteDictionariesOntoCatalogSite(
  catalogSite: StorefrontCatalogSiteSettings,
  dict: StorefrontSiteDictionariesSettings,
): StorefrontCatalogSiteSettings {
  return {
    ...catalogSite,
    // Legacy flag: filters in catalog browse (not the master menu switch).
    showOptionSetFilters: dict.enabled && dict.showInCatalog,
    optionSetFilterKeys: [...dict.optionSetFilterKeys],
    filterChipsColorful: dict.chipsColorful,
    showOptionSetsOnProductCard: dict.showOnProductCard,
  };
}

/**
 * Catalog strip + menu search always use chips.
 * In `tiles` mode, recommendations own the squares; catalog/search get large mono chips.
 */
export function siteDictionaryCatalogChipChrome(
  dict: StorefrontSiteDictionariesSettings,
  surface: 'catalog' | 'search',
): {
  displayMode: 'chips';
  chipsColorful: boolean;
  size: 'sm' | 'md' | 'lg';
} {
  const curatedTiles = dict.displayMode === 'tiles';
  if (curatedTiles) {
    return {
      displayMode: 'chips',
      chipsColorful: false,
      size: 'lg',
    };
  }
  return {
    displayMode: 'chips',
    chipsColorful: dict.chipsColorful,
    size: surface === 'search' ? 'sm' : 'md',
  };
}
